import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { SignIn } from "@phosphor-icons/react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate("/");
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:block relative" style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1749244768351-2726dc23d26c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBpbnZlbnRvcnl8ZW58MHx8fHwxNzc3OTc2MDQ3fDA&ixlib=rb-4.1.0&q=85')",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}>
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="inline-flex items-center gap-2">
            <div className="w-9 h-9 bg-white text-slate-900 grid place-items-center font-heading font-bold text-lg rounded-md">V</div>
            <div>
              <div className="font-heading font-bold leading-tight">VWA Stock Manager</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate-300">Vinyl Wrapping Australia</div>
            </div>
          </div>
          <div className="space-y-3 max-w-md">
            <div className="text-xs font-mono tracking-[0.2em] uppercase text-blue-300">VWA Stock Manager</div>
            <h1 className="font-heading text-5xl font-bold leading-tight">Precision tracking for every meter.</h1>
            <p className="text-slate-300 text-base">Manage rolls by manufacturer, series, color, batch, and manufacturing date — down to the centimeter.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Sign in</div>
            <h2 className="font-heading text-3xl font-bold text-slate-900">Staff Login</h2>
            <p className="text-sm text-slate-600 mt-2">Access the inventory management dashboard.</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input" placeholder="admin@vinylstock.com" className="h-11 rounded-md" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input" placeholder="••••••••" className="h-11 rounded-md" />
            </div>
          </div>

          <Button type="submit" disabled={submitting}
            className="w-full h-11 bg-blue-600 hover:bg-blue-700 rounded-md gap-2" data-testid="login-submit-btn">
            <SignIn size={18} /> {submitting ? "Signing in…" : "Sign in"}
          </Button>

          <div className="border border-slate-200 bg-slate-50 rounded-md p-3 text-xs text-slate-600 text-center">
            Don't have an account? Contact your workspace admin.
          </div>
        </form>
      </div>
    </div>
  );
}
